import { star } from "@/figmaElements";
import { calculateAccelerate } from "@/functions/calculateAccelerate";
import { calculatePlayerStatsBonus } from "@/functions/calculatePlayerStatsBonus";
import { capitalizeWords } from "@/functions/capitalizeFirstLetters";
import { chemStyles } from "@/functions/getChemStyles";
import { cn } from "@/lib/utils";
import { DetailedPlayerAction } from "@/pages/DetailedPlayer/reducer";
import { chemStylesToIcon } from "@/redux/squadBuilder/squadBuilderUtils";
import { useEffect, useState } from "react";

type Props = {
  chemistry: number;
  height: number;
  inGamePlayer: { [label: string]: number };
  faceCardStats: number[];
  bestChemStyle: number;
  selectedChemStyleId: number;
  hoveredChemStyleId: number;
  handleAction: (action: DetailedPlayerAction) => void;
  isGk: boolean;
  setAccelerate: (accelerate: string) => void;
};

const ChemstyleButton = ({
  id,
  handleAction,
  selected,
  best,
}: {
  id: number;
  handleAction: (action: DetailedPlayerAction) => void;
  selected: boolean;
  best: boolean;
}) => (
  <button
    className={cn(
      "flex flex-row justify-start gap-x-2 w-32 px-2 h-9 mobile:h-7 items-center bg-action-tertiary rounded-md shrink-0 text-dark-secondary",
      selected
        ? "bg-action-tertiary-active text-dark-primary"
        : "hover:bg-action-tertiary-hover hover:text-dark-primary",
    )}
    onClick={() => handleAction({ type: "SET_CHEMSTYLE", payload: id })}
    onMouseEnter={() =>
      handleAction({ type: "SET_TEMPORARY_CHEMSTYLE", payload: id })
    }
  >
    <div
      className="text-xl"
      style={{
        fontFamily: "UltimateTeamIcons",
      }}
    >
      {chemStylesToIcon[id]}
    </div>
    <p className="text-xs">{capitalizeWords(chemStyles[id])}</p>
    {best && <div className="w-3 h-3 ml-auto">{star}</div>}
  </button>
);

const getAccelerates = ({
  chemStyleId,
  chemistry,
  inGamePlayer,
  faceCardStats,
  height,
}: {
  chemStyleId: number;
  chemistry: number;
  inGamePlayer: { [label: string]: number };
  faceCardStats: number[];
  height: number;
}) => {
  const specificStatsBonus = calculatePlayerStatsBonus(
    chemStyleId,
    chemistry,
    inGamePlayer,
    faceCardStats,
  )?.["specificStatsBonus"];
  const playerAgility =
    inGamePlayer["agility"] + (specificStatsBonus?.["agility"] || 0);
  const playerStrength =
    inGamePlayer["strength"] + (specificStatsBonus?.["strength"] || 0);
  const playerAcceleration =
    inGamePlayer["acceleration"] + (specificStatsBonus?.["acceleration"] || 0);

  if (playerAcceleration && playerStrength && playerAgility) {
    const accelerate = calculateAccelerate(
      playerAgility < 100 ? playerAgility : 99,
      playerAcceleration < 100 ? playerAcceleration : 99,
      playerStrength < 100 ? playerStrength : 99,
      height,
    );
    return accelerate;
  }
};

export const ChemStyles = ({
  chemistry,
  selectedChemStyleId,
  hoveredChemStyleId,
  height,
  inGamePlayer,
  faceCardStats,
  bestChemStyle,
  handleAction,
  isGk,
  setAccelerate,
}: Props) => {
  const [controlledLengthy, setControlledLengthy] = useState([]);
  const [mostlyLengthy, setMostlyLengthy] = useState([]);
  const [lengthyAttributes, setLengthyAttributes] = useState([]);
  const [controlledAttributes, setControlledAttributes] = useState([]);
  const [controlledExplosive, setControlledExplosive] = useState([]);
  const [mostlyExplosive, setMostlyExplosive] = useState([]);
  const [explosiveAttributes, setExplosiveAttributes] = useState([]);
  const [entries] = useState(Object.entries(chemStyles));
  useEffect(() => {
    setAccelerate(
      getAccelerates({
        chemStyleId: hoveredChemStyleId || selectedChemStyleId,
        chemistry,
        inGamePlayer,
        faceCardStats,
        height,
      }),
    );
  }, [
    hoveredChemStyleId,
    selectedChemStyleId,
    chemistry,
    inGamePlayer,
    faceCardStats,
    height,
  ]);

  useEffect(() => {
    const newControlledLengthy = [];
    const newMostlyLengthy = [];
    const newLengthyAttributes = [];
    const newControlledAttributes = [];
    const newControlledExplosive = [];
    const newMostlyExplosive = [];
    const newExplosiveAttributes = [];

    for (let i = 0; i < entries.length; i += 1) {
      const chemStyle = {
        id: +entries[i][0],
        name: entries[i][1],
      };

      if (!isGk && chemStyle.id >= 269) {
        break;
      }
      if (isGk && chemStyle.id < 269) {
        continue;
      }

      const chemStyleButton = (
        <ChemstyleButton
          best={chemStyle.id === bestChemStyle}
          key={chemStyle.id}
          id={chemStyle.id}
          handleAction={handleAction}
          selected={chemStyle.id === selectedChemStyleId}
        />
      );

      const currentEntryAccelerate = getAccelerates({
        chemStyleId: chemStyle.id,
        chemistry: chemistry === 0 ? 3 : chemistry,
        inGamePlayer,
        faceCardStats,
        height,
      });

      if (currentEntryAccelerate === "Controlled Lengthy") {
        newControlledLengthy.push(chemStyleButton);
      }
      if (currentEntryAccelerate === "Mostly Lengthy") {
        newMostlyLengthy.push(chemStyleButton);
      }
      if (currentEntryAccelerate === "Lengthy") {
        newLengthyAttributes.push(chemStyleButton);
      }
      if (currentEntryAccelerate === "Controlled") {
        newControlledAttributes.push(chemStyleButton);
      }
      if (currentEntryAccelerate === "Controlled Explosive") {
        newControlledExplosive.push(chemStyleButton);
      }
      if (currentEntryAccelerate === "Mostly Explosive") {
        newMostlyExplosive.push(chemStyleButton);
      }
      if (currentEntryAccelerate === "Explosive") {
        newExplosiveAttributes.push(chemStyleButton);
      }
    }

    setControlledLengthy(newControlledLengthy);
    setMostlyLengthy(newMostlyLengthy);
    setLengthyAttributes(newLengthyAttributes);
    setControlledAttributes(newControlledAttributes);
    setControlledExplosive(newControlledExplosive);
    setMostlyExplosive(newMostlyExplosive);
    setExplosiveAttributes(newExplosiveAttributes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedChemStyleId,
    hoveredChemStyleId,
    chemistry,
    bestChemStyle,
    inGamePlayer,
  ]);

  const chemStylesSection = (
    label: string,
    chemStyles: any,
    handleAction: (action: DetailedPlayerAction) => void,
  ) => (
    <div
      className="flex flex-col"
      style={{
        width: `${Math.ceil(chemStyles.length / 4) * 132}px`,
      }}
    >
      <div className="text-xs text-dark-tertiary">{label}</div>
      <div
        className="flex flex-wrap gap-1"
        onMouseLeave={() =>
          handleAction({ type: "SET_TEMPORARY_CHEMSTYLE", payload: undefined })
        }
      >
        {chemStyles}
      </div>
    </div>
  );

  return (
    <div className="flex flex-row gap-x-2 mobile:gap-x-4">
      {lengthyAttributes.length > 0 &&
        chemStylesSection("Lengthy", lengthyAttributes, handleAction)}
      {mostlyLengthy.length > 0 &&
        chemStylesSection("Mostly Lengthy", mostlyLengthy, handleAction)}
      {controlledLengthy.length > 0 &&
        chemStylesSection(
          "Controlled Lengthy",
          controlledLengthy,
          handleAction,
        )}
      {controlledAttributes.length > 0 &&
        chemStylesSection("Controlled", controlledAttributes, handleAction)}

      {controlledExplosive.length > 0 &&
        chemStylesSection(
          "Controlled Explosive",
          controlledExplosive,
          handleAction,
        )}
      {mostlyExplosive.length > 0 &&
        chemStylesSection("Mostly Explosive", mostlyExplosive, handleAction)}
      {explosiveAttributes.length > 0 &&
        chemStylesSection("Explosive", explosiveAttributes, handleAction)}
    </div>
  );
};
